import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { ArrowRightLeft, Loader2, Plus, Trash2, X } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type RuleType = "redirect" | "rewrite" | "header";

interface RouteRule {
  id: string;
  ruleType: RuleType;
  source: string;
  destination: string | null;
  statusCode: number | null;
  headers: Record<string, string> | null;
  conditions: RuleConditions | null;
  position: number;
  enabled: boolean;
  createdAt: string;
}

/** 008 (US3) — has/missing matching, mirrored from packages/shared. */
type ConditionType = "header" | "cookie" | "query" | "host";
interface RuleCondition {
  type: ConditionType;
  key?: string;
  value?: string;
}
interface RuleConditions {
  has?: RuleCondition[];
  missing?: RuleCondition[];
}
interface ConditionRow extends RuleCondition {
  kind: "has" | "missing";
}

/** Render a condition the way it reads aloud, for the rule list. */
function describeCondition(c: RuleCondition, kind: "has" | "missing"): string {
  const subject = c.type === "host" ? "host" : `${c.type} ${c.key}`;
  if (c.type === "host") return `${kind === "has" ? "host is" : "host is not"} ${c.value}`;
  if (c.value === undefined) return kind === "has" ? `has ${subject}` : `no ${subject}`;
  return `${subject} ${kind === "has" ? "=" : "\u2260"} ${c.value}`;
}

const TYPE_BADGE: Record<RuleType, string> = {
  redirect: "bg-accent-subtle text-accent border border-accent/30",
  rewrite: "bg-done-subtle text-done border border-done/30",
  header: "bg-subtle text-subtle-foreground border border-border-muted",
};

const SELECT_CLS = "h-9 px-3 rounded-md border border-border bg-background text-sm font-mono";

/**
 * Rules tab — project-level redirects, rewrites and response headers.
 * Rules are pushed to the edge on save, so changes go live in seconds
 * without a redeploy. The API returns rules ordered by position; the
 * server rejects loops (redirect_loop) and malformed patterns (invalid_rule)
 * with human-readable messages we surface verbatim.
 */
export function RouteRules() {
  const { org, project } = useParams<{ org: string; project: string }>();
  const qc = useQueryClient();

  const rules = useQuery({
    queryKey: ["route-rules", org, project],
    queryFn: () =>
      api.get<{ rules: RouteRule[] }>(`/orgs/${org}/projects/${project}/route-rules`).then((r) => r.rules),
  });

  const toggleEnabled = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.patch<{ rule: RouteRule }>(`/orgs/${org}/projects/${project}/route-rules/${id}`, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["route-rules", org, project] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/orgs/${org}/projects/${project}/route-rules/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["route-rules", org, project] }),
  });

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-sm font-semibold inline-flex items-center gap-1.5">
          <ArrowRightLeft className="h-3.5 w-3.5 text-muted-foreground" /> Rules
        </h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          Redirects, rewrites and response headers — applied at the edge in seconds, no redeploy.
        </p>
      </div>

      <CreateRuleForm
        orgSlug={org!}
        projectSlug={project!}
        onCreated={() => qc.invalidateQueries({ queryKey: ["route-rules", org, project] })}
      />

      <div className="of-card">
        <div className="of-card-header">
          <h3 className="text-sm font-semibold">Configured rules</h3>
          <span className="text-2xs text-subtle-foreground">
            {rules.data?.length ?? 0} · evaluated top to bottom
          </span>
        </div>
        {toggleEnabled.error instanceof ApiError && (
          <div className="px-4 py-2 text-xs text-danger border-b border-border-muted">{toggleEnabled.error.message}</div>
        )}
        {remove.error instanceof ApiError && (
          <div className="px-4 py-2 text-xs text-danger border-b border-border-muted">{remove.error.message}</div>
        )}
        {rules.isLoading && <Empty>Loading…</Empty>}
        {!rules.isLoading && (rules.data?.length ?? 0) === 0 && (
          <Empty>No rules yet. Add a redirect to see it live within seconds.</Empty>
        )}
        {rules.data?.map((r) => (
          <div key={r.id} className="of-row gap-3 items-center flex-wrap sm:flex-nowrap">
            <div className="min-w-0 basis-full sm:basis-auto sm:flex-1 flex items-center gap-2">
              <span
                className={`text-2xs uppercase tracking-wider font-mono rounded px-1.5 py-0.5 shrink-0 ${TYPE_BADGE[r.ruleType]}`}
              >
                {r.ruleType}
              </span>
              <span className="font-mono text-sm truncate">
                {r.source}
                {r.ruleType === "header" ? (
                  <span className="text-subtle-foreground"> · {Object.keys(r.headers ?? {}).join(", ")}</span>
                ) : (
                  <>
                    {" "}
                    <span className="text-subtle-foreground">→</span> {r.destination}
                  </>
                )}
              </span>
              {(r.conditions?.has?.length || r.conditions?.missing?.length) ? (
                <span className="flex flex-wrap gap-1 shrink-0">
                  {(r.conditions.has ?? []).map((c, i) => (
                    <span key={`h${i}`} className="text-2xs font-mono rounded px-1.5 py-0.5 bg-subtle text-subtle-foreground border border-border-muted">
                      {describeCondition(c, "has")}
                    </span>
                  ))}
                  {(r.conditions.missing ?? []).map((c, i) => (
                    <span key={`m${i}`} className="text-2xs font-mono rounded px-1.5 py-0.5 bg-subtle text-subtle-foreground border border-border-muted">
                      {describeCondition(c, "missing")}
                    </span>
                  ))}
                </span>
              ) : null}
              {r.ruleType === "redirect" && r.statusCode != null && (
                <span className="text-2xs font-mono text-subtle-foreground tabular-nums shrink-0">{r.statusCode}</span>
              )}
            </div>
            <label className="text-xs flex items-center gap-1 cursor-pointer shrink-0">
              <input
                type="checkbox"
                checked={r.enabled}
                onChange={(e) => toggleEnabled.mutate({ id: r.id, enabled: e.target.checked })}
              />
              enabled
            </label>
            <Button
              variant="ghost"
              size="icon"
              className="text-danger"
              disabled={remove.isPending}
              onClick={() => {
                if (window.confirm(`Delete this ${r.ruleType} rule? It stops applying at the edge within seconds.`))
                  remove.mutate(r.id);
              }}
            >
              {remove.isPending && remove.variables === r.id ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

interface HeaderRow {
  name: string;
  value: string;
}

function CreateRuleForm({
  orgSlug,
  projectSlug,
  onCreated,
}: {
  orgSlug: string;
  projectSlug: string;
  onCreated: () => void;
}) {
  const [ruleType, setRuleType] = useState<RuleType>("redirect");
  const [source, setSource] = useState("");
  const [destination, setDestination] = useState("");
  const [statusCode, setStatusCode] = useState("308");
  const [headerRows, setHeaderRows] = useState<HeaderRow[]>([{ name: "", value: "" }]);
  const [conditionRows, setConditionRows] = useState<ConditionRow[]>([]);

  const create = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = { ruleType, source };
      if (ruleType === "header") {
        const headers: Record<string, string> = {};
        for (const row of headerRows) if (row.name.trim()) headers[row.name.trim()] = row.value;
        body.headers = headers;
      } else {
        body.destination = destination;
        if (ruleType === "redirect") body.statusCode = Number(statusCode);
      }
      // An empty value means "presence only", so it is omitted rather than
      // sent as "" — which the server would read as "must equal empty".
      const has = conditionRows
        .filter((c) => c.kind === "has" && (c.type === "host" ? c.value : c.key))
        .map(({ kind: _kind, ...c }) => (c.value ? c : { type: c.type, key: c.key }));
      const missing = conditionRows
        .filter((c) => c.kind === "missing" && (c.type === "host" ? c.value : c.key))
        .map(({ kind: _kind, ...c }) => (c.value ? c : { type: c.type, key: c.key }));
      if (has.length || missing.length) {
        body.conditions = {
          ...(has.length ? { has } : {}),
          ...(missing.length ? { missing } : {}),
        };
      }
      return api.post<{ rule: RouteRule }>(`/orgs/${orgSlug}/projects/${projectSlug}/route-rules`, body);
    },
    onSuccess: () => {
      setSource("");
      setDestination("");
      setHeaderRows([{ name: "", value: "" }]);
      setConditionRows([]);
      onCreated();
    },
  });

  const updateRow = (i: number, patch: Partial<HeaderRow>) =>
    setHeaderRows((cur) => cur.map((row, j) => (j === i ? { ...row, ...patch } : row)));

  const canSubmit =
    source.trim().length > 0 &&
    (ruleType === "header" ? headerRows.some((row) => row.name.trim()) : destination.trim().length > 0);

  return (
    <details className="of-card">
      <summary className="of-card-header cursor-pointer list-none">
        <h3 className="text-sm font-semibold inline-flex items-center gap-1.5">
          <Plus className="h-3.5 w-3.5" /> Add rule
        </h3>
      </summary>
      <div className="px-4 py-3 space-y-3">
        <div className="space-y-1">
          <Label htmlFor="rule-type" className="text-xs">Type</Label>
          <select
            id="rule-type"
            className={SELECT_CLS}
            value={ruleType}
            onChange={(e) => setRuleType(e.target.value as RuleType)}
          >
            <option value="redirect">redirect</option>
            <option value="rewrite">rewrite</option>
            <option value="header">header</option>
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="rule-source" className="text-xs">
            {ruleType === "header" ? "Source path pattern" : "Source path"}
          </Label>
          <Input
            id="rule-source"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            placeholder={ruleType === "redirect" ? "/old or /blog/:slug" : ruleType === "rewrite" ? "/docs/:path" : "/assets/:path"}
            className="font-mono text-sm"
          />
          <div className="text-2xs text-subtle-foreground">
            Supports :param segments and regex — captured values usable in the destination as $1.
          </div>
        </div>
        {ruleType !== "header" && (
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2">
            <div className="space-y-1">
              <Label htmlFor="rule-destination" className="text-xs">
                {ruleType === "rewrite" ? "Destination path" : "Destination"}
              </Label>
              <Input
                id="rule-destination"
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                placeholder={ruleType === "rewrite" ? "/index.html (must start with /)" : "/new, /posts/:slug or https://example.com/new"}
                className="font-mono text-sm"
              />
            </div>
            {ruleType === "redirect" && (
              <div className="space-y-1">
                <Label htmlFor="rule-status" className="text-xs">Status</Label>
                <select
                  id="rule-status"
                  className={SELECT_CLS}
                  value={statusCode}
                  onChange={(e) => setStatusCode(e.target.value)}
                >
                  <option value="308">308 — permanent</option>
                  <option value="301">301 — permanent (legacy)</option>
                  <option value="302">302 — temporary (legacy)</option>
                  <option value="307">307 — temporary</option>
                </select>
              </div>
            )}
          </div>
        )}
        {ruleType === "header" && (
          <div className="space-y-1.5">
            <Label className="text-xs">Response headers</Label>
            {headerRows.map((row, i) => (
              <div key={i} className="flex flex-wrap sm:flex-nowrap items-center gap-2">
                <Input
                  value={row.name}
                  onChange={(e) => updateRow(i, { name: e.target.value })}
                  placeholder="Cache-Control"
                  className="font-mono text-sm md:max-w-56"
                />
                <Input
                  value={row.value}
                  onChange={(e) => updateRow(i, { value: e.target.value })}
                  placeholder="public, max-age=31536000"
                  className="font-mono text-sm flex-1"
                />
                {headerRows.length > 1 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    title="Remove header"
                    onClick={() => setHeaderRows((cur) => cur.filter((_, j) => j !== i))}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            ))}
            <Button variant="ghost" size="sm" onClick={() => setHeaderRows((cur) => [...cur, { name: "", value: "" }])}>
              <Plus className="h-3.5 w-3.5" /> add header
            </Button>
          </div>
        )}
        <div className="space-y-2 border-t border-border-muted pt-3">
          <Label className="text-xs">Conditions (optional)</Label>
          <p className="text-2xs text-muted-foreground">
            The rule applies only when every condition holds. Leave a value empty to match on
            presence alone.
          </p>
          {conditionRows.map((c, i) => (
            <div key={i} className="flex items-center gap-2">
              <select
                className={SELECT_CLS}
                value={c.kind}
                onChange={(e) =>
                  setConditionRows((cur) =>
                    cur.map((row, j) => (j === i ? { ...row, kind: e.target.value as "has" | "missing" } : row)),
                  )
                }
              >
                <option value="has">has</option>
                <option value="missing">missing</option>
              </select>
              <select
                className={SELECT_CLS}
                value={c.type}
                onChange={(e) =>
                  setConditionRows((cur) =>
                    cur.map((row, j) => (j === i ? { ...row, type: e.target.value as ConditionType } : row)),
                  )
                }
              >
                <option value="header">header</option>
                <option value="cookie">cookie</option>
                <option value="query">query</option>
                <option value="host">host</option>
              </select>
              {c.type !== "host" && (
                <Input
                  className="font-mono"
                  placeholder="name"
                  value={c.key ?? ""}
                  onChange={(e) =>
                    setConditionRows((cur) =>
                      cur.map((row, j) => (j === i ? { ...row, key: e.target.value } : row)),
                    )
                  }
                />
              )}
              <Input
                className="font-mono"
                placeholder={c.type === "host" ? "example.com" : "value (optional)"}
                value={c.value ?? ""}
                onChange={(e) =>
                  setConditionRows((cur) =>
                    cur.map((row, j) => (j === i ? { ...row, value: e.target.value } : row)),
                  )
                }
              />
              <Button
                variant="ghost"
                size="icon"
                title="Remove condition"
                onClick={() => setConditionRows((cur) => cur.filter((_, j) => j !== i))}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setConditionRows((cur) => [...cur, { kind: "has", type: "header", key: "", value: "" }])}
          >
            <Plus className="h-3.5 w-3.5" /> add condition
          </Button>
        </div>
        {create.error instanceof ApiError && <div className="text-xs text-danger">{create.error.message}</div>}
        <div className="flex justify-end">
          <Button variant="primary" size="sm" onClick={() => create.mutate()} disabled={!canSubmit || create.isPending}>
            {create.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Create rule
          </Button>
        </div>
      </div>
    </details>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="px-4 py-10 text-center text-sm text-muted-foreground">{children}</div>;
}
